import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { PRODUCT_REFERENCE, ProductReference } from "@/data/productReference";
import { Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";

export const ProductComparisonGenerator = () => {
  const products = Object.values(PRODUCT_REFERENCE);
  const [selectedProducts, setSelectedProducts] = useState<ProductReference[]>([]);
  const [comparison, setComparison] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  const handleProductToggle = (product: ProductReference) => {
    setSelectedProducts(prev => {
      const isSelected = prev.some(p => p.internalCode === product.internalCode);
      if (isSelected) {
        return prev.filter(p => p.internalCode !== product.internalCode);
      } else {
        return [...prev, product];
      }
    });
  };

  const generateComparison = async () => {
    if (selectedProducts.length < 2) {
      toast({
        title: "Selection Required",
        description: "Please select at least 2 products to compare",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    setComparison("");

    try {
      const { data, error } = await supabase.functions.invoke("product-comparison", {
        body: { products: selectedProducts },
      });

      if (error) throw error;

      setComparison(data.comparison);
      toast({
        title: "Comparison Generated",
        description: "Product comparison table created successfully",
      });
    } catch (error) {
      console.error("Error generating comparison:", error);
      toast({
        title: "Generation Failed",
        description: error instanceof Error ? error.message : "Failed to generate comparison",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(comparison);
    toast({
      title: "Copied",
      description: "Comparison copied to clipboard",
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>AI Product Comparison Generator</CardTitle>
          <CardDescription>
            Select products to generate an AI-powered comparison table
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-sm font-medium mb-3">Select Products to Compare (minimum 2)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {products.map((product) => (
                <div key={product.internalCode} className="flex items-center space-x-2">
                  <Checkbox
                    id={product.internalCode}
                    checked={selectedProducts.some(p => p.internalCode === product.internalCode)}
                    onCheckedChange={() => handleProductToggle(product)}
                  />
                  <label
                    htmlFor={product.internalCode}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {product.avtName}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={generateComparison}
              disabled={isGenerating || selectedProducts.length < 2}
              className="w-full"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating Comparison...
                </>
              ) : (
                "Generate Comparison"
              )}
            </Button>
          </div>

          <div className="text-sm text-muted-foreground">
            Selected: {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''}
          </div>
        </CardContent>
      </Card>

      {comparison && (
        <Card>
          <CardHeader>
            <CardTitle>Generated Comparison</CardTitle>
            <CardDescription>
              AI-generated comparison table for selected products
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <ReactMarkdown>{comparison}</ReactMarkdown>
            </div>
            <Button onClick={copyToClipboard} variant="outline">
              Copy to Clipboard
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
